/**
 * AccentContrast — keep a colour's identity, guarantee it can be read.
 *
 * Statuses are colour-coded on purpose: one status keeps one colour across the
 * storyboard prose, the card overlay and the summoner rail, so a player learns
 * "Burn is that red". The palette, though, is handed out in registration order,
 * and its last two entries (`#be5046`, `#ab47bc`) measure 4.11:1 and 4.05:1
 * against the status chip's near-black ribbon — under WCAG AA for the chip's
 * small bold text. In a real match with several characters and several statuses
 * those entries are reached, and the rail becomes unreadable on a phone.
 *
 * The colour choice is design; being legible is not negotiable. So this lifts
 * LIGHTNESS only, keeps the hue, and only acts when the contrast is actually
 * short — a colour that already passes comes back byte-identical, so nothing
 * visible changes for the six that were always fine.
 *
 * Contract: tests/unit/ui/accentContrast.test.js
 */
let AccentContrast = {};

(function () {

  function parseHex(value) {
    if (typeof value !== 'string') return null;
    const hex = value.trim().replace(/^#/, '');
    const full = hex.length === 3
      ? hex.split('').map((c) => c + c).join('')
      : hex;
    if (!/^[0-9a-f]{6}$/i.test(full)) return null;
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  }

  function toHex([r, g, b]) {
    const part = (n) => Math.max(0, Math.min(255, Math.round(n)))
      .toString(16).padStart(2, '0');
    return `#${part(r)}${part(g)}${part(b)}`;
  }

  /** WCAG 2.x relative luminance. */
  function luminance([r, g, b]) {
    const channel = (value) => {
      const c = value / 255;
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  }

  function contrast(a, b) {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  }

  function toHsl([r, g, b]) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    return [((h * 60) + 360) % 360, s, l];
  }

  function fromHsl([h, s, l]) {
    if (s === 0) return [l * 255, l * 255, l * 255];
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    const [r, g, b] = (() => {
      if (h < 60) return [c, x, 0];
      if (h < 120) return [x, c, 0];
      if (h < 180) return [0, c, x];
      if (h < 240) return [0, x, c];
      if (h < 300) return [x, 0, c];
      return [c, 0, x];
    })();
    return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
  }

  /**
   * The same colour, light enough to read on `background`.
   *
   * @param {string} colour hex, 3 or 6 digits
   * @param {string} background hex the colour is drawn on
   * @param {number} minRatio WCAG contrast target (4.5 for small text)
   * @returns {string} the original string when it cannot be parsed or already
   *   passes; otherwise the same hue at a readable lightness
   */
  function ensure(colour, background, minRatio) {
    const rgb = parseHex(colour);
    const bg = parseHex(background);
    if (!rgb || !bg) return colour == null ? '' : colour;
    const target = Number(minRatio) > 0 ? Number(minRatio) : 4.5;
    if (contrast(rgb, bg) >= target) return colour;

    const [h, s, l] = toHsl(rgb);
    /* Walk lightness upward in small steps rather than solving for it: the
       luminance curve is not linear in HSL lightness, and a step search cannot
       overshoot into a colour the designer would not recognise. */
    let best = rgb;
    for (let step = l; step <= 1; step += 0.01) {
      const candidate = fromHsl([h, s, step]);
      best = candidate;
      if (contrast(candidate, bg) >= target) break;
    }
    return toHex(best);
  }

  AccentContrast = { ensure };
}());

export { AccentContrast };
